import { authDriver } from '@bp-files/client';
import { env } from '@/env';
import { postJson } from '@/lib/api';
import * as mockAuth from '@/lib/mockAuth';
import type { EnrolledPasskey, EnrollPasskeyInput } from '@bp-files/client';

/**
 * The passkey ceremony, bound to this app's transport (USR-11).
 *
 * **A second `authDriver` instance, and that is fine.** `Bootstrap.tsx` builds
 * one for the sign-in gate; this builds another for the account screen. The
 * driver is a pure switch over `env` and a mock pair — it holds no state, opens
 * no connection, and caches nothing — so two of them cost a closure. What would
 * be expensive is threading the gate's instance down through `Bootstrap` and
 * `App` to one leaf, which is prop-drilling a value neither of them uses.
 *
 * **The dynamic `import()` still happens once**, in the module the driver
 * reaches: `@simplewebauthn/browser` is loaded the first time somebody enrols
 * and never in a mocked build, which is ADR-0027's invariant and the whole
 * reason this indirection exists at all.
 *
 * The two functions are pre-bound to `postJson` here so `App.tsx` passes the
 * component one argument instead of two — the transport is this app's business
 * and not the screen's.
 *
 * **Both ceremonies used to mark the browser on success** (ADR-0050's
 * `passkeyHint.ts`), and ADR-0083 removed the mark along with the module. The
 * landing page offered the passkey only to a browser carrying it; it now offers
 * the passkey to everybody, so there is nothing for a completed ceremony to
 * record. Both functions below are the ceremony and nothing else.
 *
 * The reason the write lived here rather than in either screen is still worth
 * knowing, because it is the reason anything else cross-cutting would belong
 * here too: enrolment and assertion are performed by two components that share
 * nothing, and this module is the single place both go through.
 */
const driver = authDriver({ env, mock: mockAuth });

export function enrollPasskey(input: EnrollPasskeyInput): Promise<EnrolledPasskey> {
  return driver.enrollPasskey(postJson, input);
}

export function platformAuthenticatorAvailable(): Promise<boolean> {
  return driver.platformAuthenticatorAvailable();
}

/**
 * One gesture, one session (SYS-8).
 *
 * **Reached from the sign-in gate rather than from the app**, unlike the two
 * above — which is why it is here and not on `bpSession.ts`: this module is
 * where the switch that decides whether a WebAuthn library loads already lives,
 * and a second copy of that decision beside the gate would be the thing
 * Article 3 asks us not to have two of.
 */
export function signInWithPasskey(): Promise<{ restricted: boolean }> {
  return driver.signInWithPasskey(postJson);
}
